/**
 * avatar-upload.ts — mutation upload ảnh đại diện qua cửa ghi duy nhất BE.
 * staffId bỏ trống = đổi ảnh chính mình; truyền staffId = đổi hộ (cần hr.write).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postFormAuthed } from "@/lib/api/authed-fetch";
import { serverErrorMessage } from "@/lib/api/error-message";
import { rememberFreshUpload } from "@/lib/fresh-upload";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8080/api/v1";

export type AvatarUploadResp = {
  staff_id: string;
  base_path: string;
  public_url: string;
  variants: Record<string, { url: string } | undefined>;
};

/**
 * Trần dung lượng ảnh đại diện — khớp `maxUploadBytes` của máy chủ
 * (backend/internal/core/profile/upload.go). Đổi ở đây thì đổi cả bên đó:
 * FE cao hơn BE là người dùng ngồi chờ tải xong mới nhận lỗi; FE thấp hơn BE
 * là chặn oan thứ máy chủ vốn nhận được.
 */
export const AVATAR_MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

export function useAvatarUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      staffId,
    }: {
      file: File;
      staffId?: string;
    }): Promise<AvatarUploadResp> => {
      // Chặn tại máy người dùng để khỏi tải lên rồi mới báo hỏng.
      if (file.size > AVATAR_MAX_BYTES) throw new Error("Ảnh vượt 10MB — chọn ảnh nhỏ hơn");
      if (!ALLOWED.includes(file.type)) {
        throw new Error(`Định dạng ${file.type} không được hỗ trợ (dùng JPG/PNG/WebP)`);
      }
      const form = new FormData();
      form.append("file", file, file.name);
      const qs = staffId ? `?staff_id=${encodeURIComponent(staffId)}` : "";
      const resp = await postFormAuthed(`${API_BASE}/profile/photo/upload${qs}`, form);
      if (!resp.ok) {
        const body: unknown = await resp.json().catch(() => null);
        throw new Error(serverErrorMessage(body, "Không đổi được ảnh đại diện — thử lại"));
      }
      return (await resp.json()) as AvatarUploadResp;
    },
    onSuccess: (data) => {
      // Bản thumb/md/lg còn đang sinh; xin chúng lúc này là ăn 404 mà kho ảnh
      // ghim một năm. Nhớ ảnh gốc để avatarSrcFor vẽ bằng nó trong phiên này.
      rememberFreshUpload(data.base_path, data.variants.orig?.url ?? data.public_url);
      // staff.avatarUrl đổi → mọi StaffAvatar tự cập nhật qua query staff.
      void qc.invalidateQueries({ queryKey: ["staff"] });
    },
  });
}
